// Organization-level settings — shared across all users in the org
// Replaces localStorage-based feature_agent_assignments with DB-backed storage
// Uses localStorage as cache for fast reads, DB as source of truth

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'

const CACHE_KEY = 'org_settings_cache'
const ASSIGNMENTS_KEY = 'feature_agent_assignments'

interface OrgSettingsReturn {
  featureAssignments: Record<string, string>
  isLoading: boolean
  saveFeatureAssignments: (assignments: Record<string, string>) => Promise<void>
  clearFeatureAssignments: () => Promise<void>
}

/**
 * Get feature agent assignments synchronously (for use outside React).
 * Reads from localStorage cache (populated by useOrgSettings hook).
 */
export function getFeatureAssignments(): Record<string, string> {
  try {
    const cached = localStorage.getItem(CACHE_KEY)
    if (cached) {
      const parsed = JSON.parse(cached)
      if (parsed[ASSIGNMENTS_KEY]) return parsed[ASSIGNMENTS_KEY]
    }
    // Fallback to old localStorage key for backwards compat
    const old = localStorage.getItem('feature_agent_assignments')
    if (old) return JSON.parse(old)
  } catch { /* ignore */ }
  return {}
}

/**
 * Get a specific agent ID for a feature.
 */
export function getAgentForFeature(feature: string, fallbackEnvVar?: string): string {
  const assignments = getFeatureAssignments()
  const stored = assignments[feature] || ''
  const isValidUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(stored)
  if (isValidUuid) return stored
  if (fallbackEnvVar) return fallbackEnvVar
  return ''
}

/**
 * React hook for organization settings — loads from DB, caches in localStorage.
 */
export function useOrgSettings(): OrgSettingsReturn {
  const { user } = useAuth()
  const [featureAssignments, setFeatureAssignments] = useState<Record<string, string>>(getFeatureAssignments())
  const [isLoading, setIsLoading] = useState(true)

  // Load from DB on mount
  useEffect(() => {
    if (!user?.organizationId) {
      setIsLoading(false)
      return
    }

    const loadSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('organization_settings' as never)
          .select('setting_key, setting_value')
          .eq('organization_id', user.organizationId)
          .eq('setting_key', ASSIGNMENTS_KEY)
          .maybeSingle()

        const row = data as { setting_key: string; setting_value: Record<string, string> } | null
        if (!error && row?.setting_value) {
          const assignments = row.setting_value
          setFeatureAssignments(assignments)
          // Update cache
          updateCache(ASSIGNMENTS_KEY, assignments)
          // Also keep old localStorage key for backwards compat
          localStorage.setItem('feature_agent_assignments', JSON.stringify(assignments))
        } else {
          // No DB record — check if there's data in old localStorage to migrate
          const old = localStorage.getItem('feature_agent_assignments')
          if (old) {
            const parsed = JSON.parse(old)
            if (Object.keys(parsed).length > 0) {
              // Migrate localStorage → DB
              await saveToDb(user.organizationId, user.id, ASSIGNMENTS_KEY, parsed)
              setFeatureAssignments(parsed)
              updateCache(ASSIGNMENTS_KEY, parsed)
            }
          }
        }
      } catch (err) {
        console.warn('Failed to load org settings:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadSettings()
  }, [user?.organizationId, user?.id])

  const saveFeatureAssignments = useCallback(async (assignments: Record<string, string>) => {
    if (!user?.organizationId || !user?.id) return

    setFeatureAssignments(assignments)
    updateCache(ASSIGNMENTS_KEY, assignments)
    localStorage.setItem('feature_agent_assignments', JSON.stringify(assignments))

    await saveToDb(user.organizationId, user.id, ASSIGNMENTS_KEY, assignments)
  }, [user?.organizationId, user?.id])

  const clearFeatureAssignments = useCallback(async () => {
    if (!user?.organizationId) return

    setFeatureAssignments({})
    updateCache(ASSIGNMENTS_KEY, {})
    localStorage.removeItem('feature_agent_assignments')

    await supabase
      .from('organization_settings' as never)
      .delete()
      .eq('organization_id', user.organizationId)
      .eq('setting_key', ASSIGNMENTS_KEY)
  }, [user?.organizationId])

  return { featureAssignments, isLoading, saveFeatureAssignments, clearFeatureAssignments }
}

// ── Helpers ──

function updateCache(key: string, value: Record<string, string>) {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}')
    cached[key] = value
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached))
  } catch { /* ignore */ }
}

async function saveToDb(
  organizationId: string, userId: string, key: string, value: Record<string, string>,
) {
  const { error } = await supabase
    .from('organization_settings' as never)
    .upsert({
      organization_id: organizationId,
      setting_key: key,
      setting_value: value,
      updated_by: userId,
      updated_at: new Date().toISOString(),
    } as never, { onConflict: 'organization_id,setting_key' })

  if (error) console.error('Failed to save org setting:', error.message)
}
